import { type ReactElement, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fontFamilies, s } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';
import type { ProjectWipeDataModule } from '@/features/projects/data/projects';
import type { ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };
type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

type ModuleWipeMutation = {
  isPending: boolean;
  mutate: (
    input: {
      id: string;
      confirmation: string;
      module?: ProjectWipeDataModule;
      reasonCode?: string;
      ticket?: string;
      async?: boolean;
    },
    options?: {
      onSuccess?: (result: {
        status: 'wiped' | 'scheduled';
        jobId?: string;
        jobStatus?: 'pending' | 'running' | 'completed' | 'failed';
        recoveryHint?: string;
      }) => void;
      onError?: (err: unknown) => void;
    },
  ) => void;
};

export type ProjectModuleDataWipeSectionProps = {
  module: ProjectWipeDataModule;
  project: ProjectDto | null;
  wipeProjectData: ModuleWipeMutation;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
};

const moduleContent = {
  delivery: {
    title: 'Delivery Data Deletion',
    buttonLabel: 'Delete Data',
    reasonCode: 'DELIVERY_MODULE_USER_REQUEST',
    description: 'Delete all Delivery module data for this project.',
    dialogTitle: 'Delete Delivery Data?',
    dialogBody:
      'This action is permanent and cannot be undone. It will remove releases and features for this project.',
    successMessage: 'Wiped delivery data for',
    scheduledMessage: 'Scheduled delivery data wipe for',
    available: true,
  },
  code: {
    title: 'Code Data Deletion',
    buttonLabel: 'Delete Data',
    reasonCode: 'CODE_MODULE_USER_REQUEST',
    description: 'Delete all Code module data for this project.',
    dialogTitle: 'Delete Code Data?',
    dialogBody:
      'This action is permanent and cannot be undone. It will remove coverage runs, directories, and files for this project.',
    successMessage: 'Wiped code data for',
    scheduledMessage: 'Scheduled code data wipe for',
    available: true,
  },
  test: {
    title: 'Test Data Deletion',
    buttonLabel: 'Delete Data',
    reasonCode: 'TEST_MODULE_USER_REQUEST',
    description: 'Delete all Test module data for this project.',
    dialogTitle: 'Delete Test Data?',
    dialogBody:
      'This action is permanent and cannot be undone. It will remove uploaded results, execution runs, suites, and tests for this project.',
    successMessage: 'Wiped test data for',
    scheduledMessage: 'Scheduled test data wipe for',
    available: true,
  },
  operations: {
    title: 'Operations Data Deletion',
    buttonLabel: 'Delete Data',
    reasonCode: 'OPERATIONS_MODULE_USER_REQUEST',
    description: 'Delete all Operations module data for this project.',
    dialogTitle: 'Delete Operations Data?',
    dialogBody:
      'This action is permanent and cannot be undone. It will remove pipelines, builds, and execution data for this project.',
    successMessage: 'Wiped operations data for',
    scheduledMessage: 'Scheduled operations data wipe for',
    available: true,
  },
} as const satisfies Record<
  ProjectWipeDataModule | 'delivery' | 'code',
  {
    title: string;
    buttonLabel: string;
    reasonCode: string;
    description: string;
    dialogTitle: string;
    dialogBody: string;
    successMessage: string;
    scheduledMessage: string;
    available: true;
  }
>;

export const ProjectModuleDataWipeSection = ({
  module,
  project,
  wipeProjectData,
  mutationError,
  notify,
}: ProjectModuleDataWipeSectionProps): ReactElement => {
  const content = moduleContent[module];
  const form = useDialogFormState({ projectName: '' });
  const errors = useFormErrors(form);
  const [open, setOpen] = useState(false);
  const expectedProjectName = project?.name.trim() ?? '';
  const confirmation = project ? `WIPE_PROJECT_DATA:${project.id}:${module}` : '';
  const matchesExpectedName =
    expectedProjectName.length > 0 && form.values.projectName.trim() === expectedProjectName;

  const projectNameCodeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: fontFamilies.mono,
    backgroundColor: 'action.hover',
    borderRadius: s('xxs'),
    pl: s('sm'),
    pr: s('xs'),
  } as const;

  const destructiveActionButtonSx = {
    minWidth: { sm: '176px' },
    mt: { xs: s('sm'), sm: 0 },
    whiteSpace: 'nowrap',
    borderColor: 'error.main',
    color: 'error.main',
    '&:hover': {
      borderColor: 'error.main',
      color: 'error.main',
      backgroundColor: 'rgba(239, 68, 68, 0.08)',
    },
  } as const;

  const closeDialog = (): void => {
    if (wipeProjectData.isPending) {
      return;
    }
    form.handleClose(
      () => {
        setOpen(false);
      },
      { clearError: mutationError.clearError },
    );
  };

  const confirmProjectName = (): boolean => {
    const valid = form.validateRequired('projectName');
    if (!valid) {
      return false;
    }
    if (form.values.projectName.trim() === expectedProjectName) {
      return true;
    }
    form.setFieldError('projectName', 'Enter the exact project name to confirm.');
    return false;
  };

  return (
    <>
      <Panel title={content.title} surfaceVariant="settings" sx={settingsPanelSurfaceSx}>
        <Stack gap="md">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            align={{ sm: 'center' }}
            sx={{ gap: { xs: s('sm'), sm: s('sm') } }}
          >
            <Text role="body" color="textSecondary" sx={[settingsCopyTextSx, { flex: 1 }]}>
              {content.description}
            </Text>
            <Button
              variant="secondary"
              color="error"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || wipeProjectData.isPending}
              onClick={() => {
                mutationError.clearError();
                form.resetDialog();
                setOpen(true);
              }}
            >
              {content.buttonLabel}
            </Button>
          </Stack>
        </Stack>
      </Panel>
      <ConfirmDialog
        open={open}
        title={content.dialogTitle}
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              {content.dialogBody}
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Enter{' '}
              <Box component="code" sx={projectNameCodeSx}>
                {expectedProjectName}
              </Box>{' '}
              to confirm.
            </Text>
            <TextField
              label="Project Name"
              fieldStyle="settings"
              value={form.values.projectName}
              onChange={form.handleChange('projectName')}
              {...errors.fieldErrorProps('projectName')}
              fullWidth
            />
          </Stack>
        }
        confirmLabel={content.buttonLabel}
        confirmColor="error"
        confirmDisabled={!matchesExpectedName}
        loading={wipeProjectData.isPending}
        onClose={closeDialog}
        onConfirm={() => {
          if (!(project && confirmProjectName())) {
            return;
          }
          wipeProjectData.mutate(
            {
              id: project.id,
              confirmation,
              module,
              reasonCode: content.reasonCode,
              async: true,
            },
            {
              onSuccess: (result) => {
                mutationError.clearError();
                form.resetDialog();
                setOpen(false);
                notify({
                  message: `${
                    result.status === 'scheduled'
                      ? content.scheduledMessage
                      : content.successMessage
                  } ${project.name}`,
                  tone: result.status === 'scheduled' ? 'blue' : 'green',
                });
              },
              onError: (err) => {
                mutationError.setError(err);
              },
            },
          );
        }}
      />
    </>
  );
};
